import React, { useEffect } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { themes, ReduxState } from 'kb-shared';
import { withRouter, Link, RouteComponentProps } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
// @ts-ignore
import { MorphIcon } from 'react-svg-buttons/lib';
import NewAppointmentButton from '../../../components/NewAppointmentButton';
import { renderMenuItem, renderComingSoonMenuItem } from './components';
import ProfileImageUploadButton from '../../../components/PhotoUpload/ProfilePhoto/ProfileImageUploadButton';
import { ReactComponent as LeftBracket } from '../../../assets/icons/current_cycle_left.svg';
import { ReactComponent as RightBracket } from '../../../assets/icons/current_cycle_right.svg';
import { menuItems, comingSoonMenuItems, messagesItem } from './menuItems';
import User from './User';
import './styles.scss';
import { MenuItem } from './menuItems';
import { MESSAGING } from 'config/features';

const { colors } = themes;
const ClosedNavItems = styled('div')`
  display: none;

  @media (min-width: 1084px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

interface MenuItemWrapperProps {
  disabled?: boolean;
  active?: boolean;
}

const MenuItemWrapper = styled.div`
  margin-bottom: 40px;
  cursor: ${(p: MenuItemWrapperProps) => (p.disabled ? 'not-allowed' : 'pointer')};
  position: relative;

  svg > path {
    fill: ${(p: MenuItemWrapperProps) => (p.active ? 'white' : colors.KBTwilightLight)};
    stroke: ${(p: MenuItemWrapperProps) => (p.active ? 'white' : colors.KBTwilightLight)};
  }
`;

const MenuSecondary = styled.div`
  margin-top: 15px;
  margin-bottom: 30px;
  @media screen and (min-width: $bp-minDesktop) {
    margin-top: 30px;
  }

  h3 {
    color: ${({ theme }) => theme.colors.yellow};
  }
`;

const UnreadBubble = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  background: ${({ theme }) => theme.colors.KBYellow};
  border-radius: 50%;
  padding: 7px;
  line-height: 5px;
  font-size: small;
`;

const unreadMessagesCount = gql`
  query unreadMessagesCount {
    unreadMessagesCount
  }
`;

type Props = RouteComponentProps & {
  match: Record<string, unknown>;
  menuItems: Array<MenuItem>;
  // @ts-ignore
  comingSoon: Array<MenuItemWrapper>;
  isMenuOpen: boolean;
  isLoggedIn: boolean;
  toggleMenu: () => void;
};

const Navigation = ({
  isMenuOpen,
  location: { pathname },
  history,
  menuItems,
  comingSoon,
  toggleMenu,
  isLoggedIn
}: Props) => {
  const { loading, data, startPolling, stopPolling } = useQuery(unreadMessagesCount, {
    skip: !isLoggedIn
  });

  useEffect(() => {
    isLoggedIn ? startPolling(10000) : stopPolling();

    return () => stopPolling();
  }, [isLoggedIn, startPolling, stopPolling]);

  if (!isLoggedIn || loading) {
    return null;
  }

  return (
    <div className={`menu ${isMenuOpen ? 'menu--open' : 'menu--closed'}`}>
      <div className="menu__contents">
        <div className={'menu__open__icon'}>
          <div className="menu__icon--container" onClick={toggleMenu}>
            <LeftBracket height={45} width={20} id="menu__icon--left-bracket" />
            <div className="menu__icon--main-icon">
              <MorphIcon type={isMenuOpen ? 'cross' : 'bars'} thickness={2} color={'white'} />
            </div>
            <RightBracket height={45} width={20} id="menu__icon--right-bracket" />
          </div>
        </div>
        {!isMenuOpen && (
          <ClosedNavItems>
            {menuItems.map((item: MenuItem) => {
              const Icon = item.icon;
              return (
                <MenuItemWrapper
                  key={item.to}
                  active={pathname === item.to}
                  // @ts-ignore
                  onClick={() => history.push(item.to)}
                >
                  {Icon && <Icon />}
                  {item.notificationKey && data?.[item.notificationKey] > 0 && (
                    <UnreadBubble>{data[item.notificationKey]}</UnreadBubble>
                  )}
                </MenuItemWrapper>
              );
            })}
          </ClosedNavItems>
        )}
        <div className="menu__inner">
          <NewAppointmentButton history={history} />
          <div className="menu__user">
            <ProfileImageUploadButton size={100} mode="placeholder" />
            <User />
          </div>

          <div className="menu__options menu__wide-container">
            {isMenuOpen && (
              <div>
                {menuItems.map(item => {
                  let notificationBubble;
                  if (item.notificationKey && data?.[item.notificationKey] > 0) {
                    notificationBubble = data?.[item.notificationKey];
                  }
                  // @ts-ignore
                  return renderMenuItem(item, true, history, pathname, notificationBubble);
                })}
              </div>
            )}
            {isMenuOpen && comingSoon.length > 0 && (
              <div>
                <h3 className="h3 section__label">Coming Soon</h3>
                {comingSoon.map(item => renderComingSoonMenuItem(item, true))}
              </div>
            )}

            <MenuSecondary>
              <Link to={'/logout'}>
                <h3 className="h3 section__label">Log out</h3>
              </Link>
            </MenuSecondary>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState) => {
  // Insert the Messaging feature if turned on.
  const messagingEnabled = state.features.features[MESSAGING];
  const menuItemsNav = [...menuItems];
  if (messagingEnabled) {
    menuItemsNav.push(messagesItem);
  }
  return {
    store: state,
    menuItems: menuItemsNav.sort((a, b) => a.order - b.order),
    comingSoon: messagingEnabled ? [] : comingSoonMenuItems,
    isLoggedIn: state.patient.isLoggedIn
  };
};

export default withRouter(connect(mapStateToProps)(Navigation));
